import { useCallback, useEffect, useRef, useState } from "react";
import { Subject } from "rxjs";
import { getUniqueId } from "@jugl-web/utils";
import type { TwilioConversationModel } from "../models/TwilioConversation/TwilioConversationModel";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import { CRMConversationMode } from "../types";

export const twilioConversations$ = new Subject<
  | { type: "prepend"; conversation: TwilioConversationModel }
  | { type: "update"; conversation: TwilioConversationModel }
  | { type: "remove"; conversationId: string }
>();

export type UseTwilioConversationsParams = UsePaginatedRequestHookParams<{
  entityId: string;
  searchTerm?: string;
  filter?:
    | CRMConversationMode.onAIAutoReply
    | CRMConversationMode.waitingResponse
    | string;
}>;

export type UseTwilioConversationsReturn =
  UsePaginatedRequestHookReturn<TwilioConversationModel>;

export const useTwilioConversations = ({
  entityId,
  filter,
  searchTerm = "",
  pageSize = 10,
}: UseTwilioConversationsParams): UseTwilioConversationsReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [conversations, setConversations] = useState<TwilioConversationModel[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchConversations] =
    integrationsApi.useLazyGetTwilioConversationsQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchConversations({
          entityId,
          page,
          filter,
          search_term: searchTerm,
          page_size: pageSize,
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setConversations((prev) => {
            // If page 1, replace all
            if (page === 1) {
              return result.data;
            }

            // Otherwise append new conversations
            const existingIds = new Set(prev.map((c) => c.conv_id));
            const newConversations = result.data.filter(
              (c) => !existingIds.has(c.conv_id)
            );

            return [...prev, ...newConversations];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchConversations, entityId, filter, searchTerm, pageSize]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setConversations([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && conversations.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, conversations.length, loadPage]);

  // Load initial page when component mounts or search term/filter changes
  useEffect(() => {
    reset();
  }, [reset, filter, searchTerm]);

  // Listen to Subject for real-time conversation updates
  useEffect(() => {
    const subscription = twilioConversations$.subscribe((action) => {
      if (action.type === "prepend") {
        const { conversation } = action;
        setConversations((prev) => {
          // Remove conversation if it already exists
          const filtered = prev.filter(
            (c) => c.conv_id !== conversation.conv_id
          );

          // Prepend conversation to the top
          return [conversation, ...filtered];
        });
      } else if (action.type === "update") {
        const { conversation } = action;
        setConversations((prev) => {
          const existingIndex = prev.findIndex(
            (c) => c.conv_id === conversation.conv_id
          );

          if (existingIndex >= 0) {
            // Update existing conversation in place
            const newConversations = [...prev];
            newConversations[existingIndex] = conversation;
            return newConversations;
          }

          // If not found, don't add it
          return prev;
        });
      } else if (action.type === "remove") {
        const { conversationId } = action;
        setConversations((prev) =>
          prev.filter((c) => c.conv_id !== conversationId)
        );
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore =
    isLoading && (currentPage > 1 || conversations.length !== 0);
  const isLoadingMoreError =
    isError && (currentPage > 1 || conversations.length === 0);

  return {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
  };
};
